import {
  createMerchantConfig,
  deleteMerchantConfig,
  getMerchantConfig,
  updateMerchantConfig,
  type AllowedModel,
  type MerchantConfig,
  type MerchantConfigCreateRequest,
  type MerchantConfigUpdateRequest,
} from '@/services/merchantConfigService';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

export const useConfigStore = defineStore('config', () => {
  const config = ref<MerchantConfig | null>(null);
  const isLoading = ref(false);
  const isSaving = ref(false);
  const error = ref<string | null>(null);

  const loadConfig = async () => {
    try {
      isLoading.value = true;
      error.value = null;
      config.value = await getMerchantConfig();
      console.log('Merchant configuration loaded:', config.value);
    } catch (err) {
      console.error('Failed to load merchant configuration:', err);
      // 404 is expected if config doesn't exist yet, not an error state
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const statusCode = (err as any)?.response?.status;
      if (statusCode !== 404) {
        error.value = 'Failed to load merchant configuration';
      }
    } finally {
      isLoading.value = false;
    }
  };

  const createConfig = async (configData: MerchantConfigCreateRequest) => {
    try {
      isSaving.value = true;
      error.value = null;
      config.value = await createMerchantConfig(configData);
      console.log('Merchant configuration created:', config.value);
      return config.value;
    } catch (err) {
      console.error('Failed to create merchant configuration:', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const statusCode = (err as any)?.response?.status;
      if (statusCode === 409) {
        error.value = 'Merchant configuration already exists';
      } else if (statusCode === 400) {
        error.value = 'Invalid configuration data';
      } else {
        error.value = 'Failed to create merchant configuration';
      }
      throw err;
    } finally {
      isSaving.value = false;
    }
  };

  const updateConfig = async (configData: MerchantConfigUpdateRequest) => {
    try {
      isSaving.value = true;
      error.value = null;
      config.value = await updateMerchantConfig(configData);
      console.log('Merchant configuration updated:', config.value);
      return config.value;
    } catch (err) {
      console.error('Failed to update merchant configuration:', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const statusCode = (err as any)?.response?.status;
      if (statusCode === 404) {
        error.value = 'Merchant configuration not found';
      } else if (statusCode === 400) {
        error.value = 'Invalid configuration data';
      } else {
        error.value = 'Failed to update merchant configuration';
      }
      throw err;
    } finally {
      isSaving.value = false;
    }
  };

  const deleteConfig = async () => {
    try {
      isSaving.value = true;
      error.value = null;
      await deleteMerchantConfig();
      config.value = null;
      console.log('Merchant configuration deleted');
    } catch (err) {
      console.error('Failed to delete merchant configuration:', err);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const statusCode = (err as any)?.response?.status;
      if (statusCode === 404) {
        error.value = 'Merchant configuration not found';
      } else {
        error.value = 'Failed to delete merchant configuration';
      }
      throw err;
    } finally {
      isSaving.value = false;
    }
  };

  const getModel = (): AllowedModel | null => {
    return config.value?.model || null;
  };

  const setModel = (model: AllowedModel) => {
    if (config.value) {
      config.value.model = model;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  const waitUntilReady = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      if (!isLoading.value) {
        resolve();
      } else {
        const unwatch = watch(isLoading, (newVal) => {
          if (!newVal) {
            unwatch();
            resolve();
          }
        });
      }
    });
  };

  return {
    config,
    isLoading,
    isSaving,
    error,
    loadConfig,
    createConfig,
    updateConfig,
    deleteConfig,
    getModel,
    setModel,
    clearError,
    waitUntilReady,
  };
});
